// Tiny, dependency-free markdown renderer for DM narration prose.
// Supports the subset the AI DM actually emits: headers (#..), bold (**),
// italic (* or _), inline code (`), horizontal rules (---), bullet/numbered
// lists, and paragraphs. Returns React nodes. Escapes raw HTML by construction
// (we only ever create text nodes + known elements), so it's XSS-safe.
import type { ReactNode } from "react";

// Keys are derived from the node's position within the parse (block index, and
// inline index within that block) so they're stable across re-renders. A
// module-global counter would hand every node a brand-new key each render,
// forcing React to unmount/remount the whole narration DOM (losing text
// selection and growing cost with log length).

// Inline: bold, italic, code. Order matters (bold before italic). `prefix`
// scopes the inline keys under their owning block.
function renderInline(text: string, prefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Tokenize on **bold**, *italic*/_italic_, `code`.
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_|`([^`]+)`)/g;
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const k = `${prefix}-i${i++}`;
    if (m[2] != null) nodes.push(<strong key={k}>{m[2]}</strong>);
    else if (m[3] != null) nodes.push(<em key={k}>{m[3]}</em>);
    else if (m[4] != null) nodes.push(<em key={k}>{m[4]}</em>);
    else if (m[5] != null) nodes.push(<code key={k} className="md-code">{m[5]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ text }: { text: string }): ReactNode {
  const lines = (text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  // Monotonic block index within THIS parse — deterministic per render.
  let bi = 0;

  const flushPara = () => {
    if (para.length) {
      const joined = para.join(" ").trim();
      if (joined) { const k = `b${bi++}`; blocks.push(<p key={k} className="md-p">{renderInline(joined, k)}</p>); }
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const lk = `b${bi++}`;
      const items = list.items.map((it, j) => <li key={`${lk}-l${j}`}>{renderInline(it, `${lk}-l${j}`)}</li>);
      blocks.push(list.ordered
        ? <ol key={lk} className="md-list">{items}</ol>
        : <ul key={lk} className="md-list">{items}</ul>);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); continue; }

    // Horizontal rule.
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) { flushPara(); flushList(); blocks.push(<hr key={`b${bi++}`} className="md-hr" />); continue; }

    // Headers.
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      flushPara(); flushList();
      const lvl = Math.min(6, h[1].length);
      const cls = `md-h md-h${lvl}`;
      const k = `b${bi++}`;
      blocks.push(<div key={k} className={cls}>{renderInline(h[2], k)}</div>);
      continue;
    }

    // List items.
    const ul = /^\s*[-*+]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (ul || ol) {
      flushPara();
      const ordered = !!ol;
      if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
      list.items.push((ul ? ul[1] : ol![1]));
      continue;
    }

    // Blockquote → treat as emphasized line.
    const bq = /^>\s?(.*)$/.exec(line);
    if (bq) { flushPara(); flushList(); const k = `b${bi++}`; blocks.push(<blockquote key={k} className="md-quote">{renderInline(bq[1], k)}</blockquote>); continue; }

    para.push(line);
  }
  flushPara(); flushList();
  return <>{blocks}</>;
}
